import moment from 'moment/moment'

import { TagProps } from '../components/app/design/Tag'
import { DataType } from '../types/DataType'
import { ConstructorArgs } from '../types/models'
import { mergeFk } from '../utils'
import { formatTimeStamp } from '../utils/dates'
import { currencyFloat } from '../utils/numbers'
import { BaseRecord } from './BaseRecord'
import { Booking } from './Booking'
import { Contract } from './Contract'
import { Line } from './Line'
import { Option } from './Option'
import { Payment } from './Payment'
import { FKType } from './SQLBuilder'
import { Signature } from './Signature'
import { Source } from './Source'
import { Transactable } from './Transactable'
import { Transaction } from './Transaction'

type EstimateConstructorArgs = ConstructorArgs<() => Estimate> & { source_display: string | null }

export class Estimate extends Transaction implements Transactable {
    readonly recordType = 'estimate'

    readonly iTransactable = true

    static readonly ESTIMATE_TOTAL_LIMIT = 500000
    rate?: number
    deposit?: number
    memo?: string
    number?: number // Integer
    duration?: number // Integer
    allowSurcharge?: boolean

    status?: string
    confirmStatus?: string
    markPaidDate?: moment.Moment
    confirmedDate?: moment.Moment
    completedDate?: moment.Moment
    invoicedDate?: moment.Moment
    scheduledDate?: moment.Moment
    paidDepositDate?: moment.Moment

    private sourceDisplay?: string

    constructor(data?: Partial<EstimateConstructorArgs>) {
        super(data)
        if (!data) {
            this.reminder = '3'
            this.channel = 'sms'
            this.date = moment()
            return
        }

        this.rate = this.propertyFromCurrencyArg(data.rate)
        this.deposit = this.propertyFromCurrencyArg(data.deposit)
        this.memo = data.memo ?? undefined
        this.number = this.propertyFromIntArg(data.number)
        this.duration = this.propertyFromIntArg(data.duration)
        this.allowSurcharge = data.allow_surcharge ?? false
        this.status = data.status ?? undefined
        this.confirmStatus = data.confirm_status ?? undefined
        this.markPaidDate = this.propertyFromTimestampArg(data.mark_paid_date)
        this.confirmedDate = this.propertyFromTimestampArg(data.confirmed_date)
        this.completedDate = this.propertyFromTimestampArg(data.completed_date)
        this.invoicedDate = this.propertyFromTimestampArg(data.invoiced_date)
        this.scheduledDate = this.propertyFromTimestampArg(data.scheduled_date)
        this.paidDepositDate = this.propertyFromTimestampArg(data.paid_deposit_date)
        this.sourceDisplay = data.source_display ?? undefined
    }

    clone(overrides?: Partial<EstimateConstructorArgs>): Estimate {
        return new Estimate({ ...this.serializeForClone(), ...overrides })
    }

    get total(): number | undefined {
        const rate = this.rate
        const deposit = this.deposit
        if (rate !== undefined && deposit !== undefined) return rate + deposit
        return rate ?? deposit
    }

    get amount(): number | undefined {
        if (this.discount && this.discount > 0)
            return (this.total ?? 0) - (this.discountAmount ?? 0)
        return this.total
    }

    get paidAmount(): number | undefined {
        if (this.markPaidDate === undefined) return 0
        let amount = this.amount ?? 0
        const deposit = this.deposit
        if (deposit !== undefined && deposit !== 0 && this.paidDepositDate !== undefined)
            amount -= deposit
        if (!!this.gratuityAmount) amount += this.gratuityAmount
        if (!!this.taxAmount) amount += this.taxAmount
        return amount
    }

    get owedAmount(): number | undefined {
        if (this.paidDate !== undefined) return 0
        let amount = this.amount ?? 0
        const deposit = this.deposit
        if (deposit !== undefined && deposit !== 0 && this.paidDepositDate !== undefined)
            amount -= deposit
        return amount
    }

    get taxableAmount(): number | undefined {
        const taxcode = this.taxcode
        if (this.lines.length > 0 && taxcode !== undefined) {
            let total = 0
            for (const line of this.lines)
                if (line.taxable && line.amount !== undefined) total += line.amount
            return total
        }
        return taxcode !== undefined ? this.amount : 0
    }

    get taxAmount(): number {
        if (this.tax && this.tax > 0) {
            const taxableTotal = this.taxableAmount
            return currencyFloat((this.tax * (taxableTotal ?? 0)) / 100)
        }
        return 0
    }

    get finalTaxAmount(): number {
        const taxcode = this.taxcode
        let taxableAmount = taxcode !== undefined ? (this.owedAmount ?? 0) : 0
        if (this.lines.length > 0 && taxcode !== undefined) {
            taxableAmount = 0
            for (const line of this.lines)
                if (line.amount && line.amount > 0 && line.taxable) taxableAmount += line.amount
        }
        if (taxableAmount > 0) {
            const taxRate =
                this.tax && this.tax > 0
                    ? this.tax
                    : this.vendor?.taxcode?.rate && this.vendor.taxcode.rate > 0
                      ? this.vendor?.taxcode.rate
                      : null
            if (taxRate) {
                const taxAmount = currencyFloat(taxRate * taxableAmount * 0.01)
                return this.paidDepositTax && this.paidDepositTax > 0
                    ? Math.max(0.0, taxAmount - this.paidDepositTax)
                    : taxAmount
            }
        }
        return 0
    }

    get discountAmount(): number | undefined {
        if (this.discount && this.discount > 0) {
            let discount = this.discount
            const total = this.discountableTotal!
            if (this.discountType === 'percent')
                discount = currencyFloat((this.discount * (total ?? 0)) / 100)
            else if (discount > total) discount = total
            return discount
        }
        return 0
    }

    get discountableTotal(): number | undefined {
        if (this.lines.length > 0) {
            let total = 0
            for (const line of this.lines)
                if (line.discountable && line.total !== undefined) total += line.total
            return total
        }
        return this.total
    }

    get gratuityAmount(): number | undefined {
        if (this.gratuity !== undefined && this.gratuity !== 0) {
            let gratuity = this.gratuity
            if (this.gratuityType === 'percent')
                if (this.amount === undefined) gratuity = 0
                else gratuity = currencyFloat((this.gratuity * this.amount) / 100)
            return gratuity
        }
        return 0
    }

    get surchargeAmount(): number {
        if (this.allowSurcharge && this.markPaidDate === undefined)
            return (
                ((this.amount ?? 0) + (this.gratuityAmount ?? 0) + (this.taxAmount ?? 0)) *
                Payment.PAYMENT_SURCHARGE_RATE
            )
        return 0
    }

    get chargeAmount(): number | undefined {
        return (
            (this.amount ?? 0) +
            (this.gratuityAmount ?? 0) +
            (this.taxAmount ?? 0) +
            (this.surchargeAmount ?? 0)
        )
    }

    get askingAmount(): number | undefined {
        return (this.amount ?? 0) + (this.taxAmount ?? 0)
    }

    get depositTaxAmount(): number | undefined {
        const taxcode = this.taxcode
        let taxableAmount = taxcode !== undefined ? (this.amount ?? 0) : 0
        if (this.lines.length > 0 && taxcode !== undefined) {
            taxableAmount = 0
            for (const line of this.lines)
                if (line.amount && line.amount > 0 && line.taxable) taxableAmount += line.amount
        }
        const deposit = this.deposit!
        if (deposit > 0) taxableAmount = deposit <= taxableAmount ? deposit : taxableAmount
        if (taxableAmount > 0)
            return this.tax && this.tax > 0 ? currencyFloat((this.tax * taxableAmount) / 100) : 0
        return 0
    }

    get depositChargeAmount(): number | undefined {
        const amount = this.deposit!
        const taxAmount = this.tax !== undefined ? currencyFloat((this.tax * amount) / 100) : 0
        const surchargeAmount = this.allowSurcharge
            ? (amount + taxAmount) * Payment.PAYMENT_SURCHARGE_RATE
            : 0
        return taxAmount + amount + surchargeAmount
    }

    get finalSurchargeAmount(): number {
        const total = this.amount ?? 0
        const amount = this.owedAmount
        const taxAmount = this.finalTaxAmount
        const gratuityAmount =
            this.gratuityType === 'percent'
                ? currencyFloat((this.gratuity! * total) / 100)
                : this.gratuity
        const surchargeAmount = this.allowSurcharge
            ? (amount + gratuityAmount + taxAmount) * Payment.PAYMENT_SURCHARGE_RATE
            : 0
        return surchargeAmount
    }

    get finalChargeAmount(): number | undefined {
        const total = this.amount ?? 0
        const amount = this.owedAmount ?? 0
        const taxAmount = this.finalTaxAmount ?? 0
        const gratuityAmount =
            this.gratuityType === 'percent' ? (this.gratuity! * total) / 100 : this.gratuity!
        const surchargeAmount = this.allowSurcharge
            ? (amount + gratuityAmount + taxAmount) * Payment.PAYMENT_SURCHARGE_RATE
            : 0
        return amount + taxAmount + gratuityAmount + surchargeAmount
    }

    get finalAskingAmount(): number | undefined {
        const amount = this.owedAmount ?? 0
        const taxAmount = this.finalTaxAmount ?? 0
        return amount + taxAmount
    }

    get sourceString(): string | undefined {
        return this.id !== undefined ? this.sourceDisplay : undefined
    }

    get statusString(): string {
        if (this.id) {
            if (this.markPaidDate && this.paymeth)
                return `Marked paid ${this.markPaidDate.format('MMM D, YYYY')} - ${this.paymeth}`
            if (this.markPaidDate) return `Marked paid ${this.markPaidDate.format('MMM D, YYYY')}`
            if (this.paidDate) return `Paid ${this.paidDate.format('MMM D, YYYY')}`
            if (this.invoicedDate) return `Invoiced ${this.invoicedDate.format('MMM D, YYYY')}`
            if (this.cancelledDate) return `Canceled ${this.cancelledDate.format('MMM D, YYYY')}`
            if (this.scheduledDate) return `Scheduled ${this.scheduledDate.format('MMM D')}`
            if (this.confirmedDate && this.paidDepositDate)
                return `Paid deposit ${this.confirmedDate.format('MMM D, YYYY')}`
            if (this.confirmedDate) return `Approved ${this.confirmedDate.format('MMM D, YYYY')}`
            if (this.status === 'confirmed') return 'Approved'
            if (this.status === 'sentQuote' && this.confirmStatus === 'confirmPayment')
                return 'Requires credit card'
            return 'Requires approval'
        }
        else return this.editStatusString
    }

    get editStatusString(): string {
        if (this.status === 'confirmed')
            return this.paidDepositDate ? 'Approved (paid deposit)' : 'Already approved'
        else if (this.status === 'sentQuote' && this.confirmStatus === 'confirmPayment')
            return 'Requires credit card'
        else if (this.status === 'sentQuote') return 'Requires approval'
        return 'Requires approval'
    }

    get statuses(): {
        title: string
        date: moment.Moment | undefined
        intent: TagProps['intent']
    }[] {
        return [
            {
                title: 'Created',
                date: this.createdDate,
                intent: 'info',
            },
            {
                title: 'Sent',
                date: this.sentDate,
                intent: 'info',
            },
            {
                title: 'Viewed',
                date: this.viewedDate,
                intent: 'warning',
            },
            {
                title: 'Accepted',
                date: this.confirmedDate,
                intent: 'success',
            },
        ]
    }

    get statusTag(): { label: string; intent: TagProps['intent'] } | null {
        const lastActiveStatus = this.statuses.findLast((status) => status.date !== undefined)
        if (!lastActiveStatus) {
            return null
        }

        return { label: lastActiveStatus.title, intent: lastActiveStatus.intent }
    }

    get hourlyRateString(): string | undefined {
        if (this.duration && this.total) {
            if (this.duration > Booking.MAX_HOURLY_DURATION)
                return `${(this.amount ?? 0) / (this.duration / 1440)} per day`
            return `${(this.amount ?? 0) / (this.duration / 60)} per hour`
        }
        return undefined
    }

    get itemTotal(): number {
        let total = 0
        for (const line of this.lines) {
            if (line.type === 'item') {
                if (line.total !== undefined) total += line.total
            }
        }
        return total
    }

    get expenseTotal(): number {
        let total = 0
        for (const line of this.lines) {
            if (line.type === 'expense') total += line.total ?? 0
        }
        return total
    }

    get itemAmount(): number {
        let total = 0
        for (const line of this.lines)
            if (line.type === 'item' && line.amount !== undefined) total += line.amount
        return total
    }

    get expenseAmount(): number {
        let total = 0
        for (const line of this.lines)
            if (line.type === 'expense' && line.amount !== undefined) total += line.amount
        return total
    }

    get itemCount(): number {
        let count = 0
        for (const line of this.lines) {
            if (line.type === 'item') count++
        }
        return count
    }

    get expenseCount(): number {
        let count = 0
        for (const line of this.lines) {
            if (line.type === 'expense') count++
        }
        return count
    }

    get description(): string {
        if (this.number !== -1) return `${this.total} . #${this.number}`
        return `${this.total}`
    }

    static reminderStrings(): Option[] {
        const strings: Option[] = []
        strings.push(new Option(undefined, { id: '1', label: 'Every day' }))
        strings.push(new Option(undefined, { id: '3', label: 'Every 3 days' }))
        strings.push(new Option(undefined, { id: '7', label: 'Once a week' }))
        return strings
    }

    get lines(): Line[] {
        return super.lines
    }

    set lines(lines: Line[]) {
        super.lines = lines
        for (const line of this.lines) {
            line.trandoc = this
        }
    }

    syncTotal() {
        let total = 0
        if (this.lines) {
            for (const line of this.lines) {
                total += line.total ?? 0
            }
        }
        if (this.deposit && this.deposit > total) {
            this.deposit = total
        }
        this.rate = total - (this.deposit ?? 0)
    }

    syncSalesTax() {
        let applyTax = false
        if (this.lines && this.lines.length > 0) {
            for (const line of this.lines) {
                if (line.taxable) {
                    applyTax = true
                    break
                }
            }
        }
        if (applyTax && this.vendor?.allowSalesTax && !this.taxcode) {
            const taxcode = this.vendor?.taxcode
            this.tax = taxcode ? taxcode.rate : undefined
            this.nexus = taxcode ? 'pro' : undefined
            this.taxcode = taxcode
        }
    }

    syncDuration() {
        let duration = 0
        if (this.lines && this.lines.length > 0) {
            for (const line of this.lines) {
                duration += (line.item!.duration ?? 0) * (line.quantity ?? 0)
            }
        }
        for (const opt of Booking.durationStrings(true, true)) {
            if (opt.id && duration <= parseInt(opt.id)) {
                duration = parseInt(opt.id)
                break
            }
        }
        this.duration = duration
    }

    deserialize(data: any) {
        super.deserialize(data)

        if (data.source) this.source = BaseRecord.fromSerializer(data.source, Source)
        if (data.contract) this.contract = BaseRecord.fromSerializer(data.contract, Contract)
        if (data.signature) this.signature = BaseRecord.fromSerializer(data.signature, Signature)
        if (data.date) this.date = moment(data.date)
        if (data.markPaidDate) this.markPaidDate = moment(data.markPaidDate)
        if (data.confirmedDate) this.confirmedDate = moment(data.confirmedDate)
        if (data.completedDate) this.completedDate = moment(data.completedDate)
        if (data.invoicedDate) this.invoicedDate = moment(data.invoicedDate)
        if (data.scheduledDate) this.scheduledDate = moment(data.scheduledDate)
        if (data.paidDepositDate) this.paidDepositDate = moment(data.paidDepositDate)
        if (data.lastModifiedDate) this.lastModifiedDate = moment(data.lastModifiedDate)
    }

    static fk: FKType = mergeFk(Transaction.fk, {
        sources: ['source'],
        contracts: ['contract'],
        signatures: ['signature'],
    })

    jsonObject(): any {
        const dict = super.jsonObject()
        dict['rate'] = this.rate ?? 0
        dict['total'] = this.total ?? 0
        dict['deposit'] = this.deposit ?? ''
        dict['date'] = formatTimeStamp(this.date)
        dict['duration'] = this.duration ?? ''
        dict['memo'] = this.memo ?? ''
        dict['status'] = this.status ?? ''
        dict['confirm_status'] = this.confirmStatus ?? ''
        dict['allow_surcharge'] = this.allowSurcharge
        dict['source'] = this.source?.id ?? ''
        dict['contract'] = this.contract?.id ?? ''
        dict['discount'] = this.discount ?? ''
        dict['discount_type'] = this.discount ? this.discountType : ''
        dict['gratuity'] = this.gratuity ?? ''
        dict['gratuity_type'] = this.gratuity ? this.gratuityType : ''
        dict['lines'] = this.lines.map((line) => line.jsonObject())
        return dict
    }

    static dataType(): DataType {
        return 'estimates'
    }

    get source(): Source | undefined {
        return this.getSourceRecord('source')
    }
    set source(source: Source | undefined) {
        this.setForeignRecord('source', source)
    }
}
